import { Inject, Injectable, isDevMode } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { TenantAuthService } from './tenant-auth.service';
import { SettingsService } from './settings.service';
import { DeviceLogLevel, DeviceLogService } from './device-log.service';
import {
  PrroLogLevel,
  PrroLogQueue,
  createPrroLogEntry,
} from './prro-log-queue.service';

const SKIP_ERROR_1 =
  'Uncaught TypeError: callback.success.apply is not a function';

// The console methods copied to the device log (DeviceLogService); the others stay as they are.
const CAPTURED_CONSOLE = ['log', 'debug', 'info', 'warn', 'error'] as const;
// Lines kept in the on-screen log panel (display_logs).
const PANEL_LINES = 300;

@Injectable({ providedIn: 'root' })
export class MonitoringService {
  // private _appInsights: ApplicationInsights;
  private _versionNumber: string;
  private taxLogger: TaxRegisterLogger;
  private _deviceUuid: string;
  private _devicePlatform: string;
  private _ionicPlatform: string;
  private _origin: string;
  private _started = false;
  // The browser's console methods, before startMonitoring wraps them: for writing to the
  // developer console without the line coming back to the device log.
  readonly nativeConsole: Pick<Console, (typeof CAPTURED_CONSOLE)[number]> = {
    log: console.log.bind(console),
    debug: console.debug.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };

  constructor(
    @Inject('ENVIRONMENT')
    private ENVIRONMENT: { appInsights: any; version: string },
    private appVersion: AppVersion,
    private _tenantAuth: TenantAuthService,
    private _settings: SettingsService,
    private _prroLogQueue: PrroLogQueue,
    private _deviceLog: DeviceLogService
  ) {}

  // The application's version: the installed one, once monitoring has started.
  get versionNumber(): string | undefined {
    return this._versionNumber || this.ENVIRONMENT.version;
  }

  startMonitoring(deviceUuid: string, platform: string, ionicPlatform: string) {
    // this._appInsights = new ApplicationInsights({
    //   config: {
    //     instrumentationKey: this.ENVIRONMENT.appInsights.instrumentationKey,
    //     disableAjaxTracking: true,
    //     // enableAutoRouteTracking: true, // option to log all route changes
    //   },
    // });
    // this._appInsights.loadAppInsights();

    this._deviceUuid = deviceUuid;
    this._devicePlatform = platform;
    this._ionicPlatform = ionicPlatform;
    this._origin = location.origin;

    // Once: a second call would wrap the wrappers and log every line twice.
    if (!this._started) {
      this._started = true;
      this._deviceLog.start({ platform, ionicPlatform, version: this.ENVIRONMENT.version });
      this._captureConsole();
    }

    if (platform == 'web_browser') {
      this._versionNumber = this.ENVIRONMENT.version;
    } else {
      this.appVersion
        .getVersionNumber()
        .then((res) => {
          this._versionNumber = res;
        })
        .catch(() => this.ENVIRONMENT.version);
    }
  }

  // console.log/debug/info/warn/error keep writing to the developer console with all their
  // arguments, and also go to the device log and the on-screen panel. The console object and its
  // other methods (table, group, time, ...) stay the browser's; before, window.console was replaced
  // by an object with only log/info/warn/error that kept the first argument, so console.debug threw.
  private _captureConsole() {
    for (const method of CAPTURED_CONSOLE) {
      const native = this.nativeConsole[method];
      const level: DeviceLogLevel = method === 'log' ? 'info' : method;
      console[method] = (...args: unknown[]) => {
        native(...args);
        this._deviceLog.add(level, 'console', args);
        this._showInPanel(level, args);
      };
    }
  }

  private _getProperties() {
    const _isDevMode = isDevMode();
    const _env = _isDevMode ? 'Development' : 'Production';
    const tenantId = this._tenantAuth.tenantId;

    return {
      companyId: tenantId,
      companyNumber: tenantId,
      deviceId: this._deviceUuid,
      // userId: this.terminalClient.loggedInEmployee?.userid,
      environment: _env,
      platform: this._devicePlatform,
      version: this._versionNumber,
      ionicPlatform: this._ionicPlatform,
      origin: this._origin,
    };
  }

  // logPageView(name?: string, url?: string) {
  //   // option to call manually
  //   this._appInsights.trackPageView({
  //     name: name,
  //     uri: url,
  //   });
  // }

  logEvent(name: string, properties?: { [key: string]: any }) {
    //this._appInsights.trackEvent({ name: name }, properties);
  }

  logMetric(
    name: string,
    average: number,
    properties?: { [key: string]: any }
  ) {
    //this._appInsights.trackMetric({ name: name, average: average }, properties);
  }

  // An error for the device log: severity 2 (Application Insights' Warning) is a warning.
  logException(
    exception: any,
    severityLevel?: number,
    properties?: { [key: string]: any }
  ) {
    // TODO: find and resolve the reason of this error
    if (exception?.message == 'Uncaught (in promise): () => err') {
      return;
    }

    const level: DeviceLogLevel = severityLevel === 2 ? 'warn' : 'error';
    const { source, ...context } = properties || {};
    this._deviceLog.add(level, source || 'app', [exception], context);
    this._showInPanel(level, [exception]);

    // Application Insights is off; with it: Object.assign({}, this._getProperties(), properties).
    // this._appInsights.trackException(
    //   {
    //     exception: exception,
    //     severityLevel: severityLevel,
    //   },
    //   props
    // );
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    const { source, ...context } = properties || {};
    this._deviceLog.add('info', source || 'app', [message], context);
    this._showInPanel('info', [message]);

    //this._appInsights.trackTrace({ message: message }, props);
  }

  private _console: HTMLElement;

  private _getConsole(): HTMLElement {
    if (this._console) {
      return this._console;
    }

    this._console = document.createElement('DIV');
    const tools = document.createElement('DIV');
    const resizeTools = document.createElement('DIV');
    resizeTools.onclick = () => {
      if (this._console.style.bottom && this._console.style.right) {
        this._console.style.bottom = '10px';
        this._console.style.right = null;
        this._console.style.left = '10px';
      } else if (this._console.style.bottom && this._console.style.left) {
        this._console.style.bottom = null;
        this._console.style.top = '10px';
        this._console.style.left = '10px';
      } else if (this._console.style.top && this._console.style.left) {
        this._console.style.left = null;
        this._console.style.top = '10px';
        this._console.style.right = '10px';
      } else {
        this._console.style.left = null;
        this._console.style.top = null;
        this._console.style.bottom = '10px';
        this._console.style.right = '10px';
      }
    };
    resizeTools.innerText = 'R';
    resizeTools.style.padding = '4px';
    resizeTools.style.borderRadius = '2px';
    resizeTools.style.border = '1px solid white';
    resizeTools.style.display = 'inline-block';
    resizeTools.style.marginRight = '4px';
    resizeTools.style.cursor = 'pointer';

    tools.appendChild(resizeTools);

    const minimizeTools = document.createElement('DIV');
    minimizeTools.onclick = () => {
      if (this._console.style.height == '300px') {
        this._console.style.height = '95%';
        this._console.style.width = '80%';
      } else if (this._console.style.height == '95%') {
        this._console.style.width = '500px';
        this._console.style.height = '100px';
      } else {
        this._console.style.width = '500px';
        this._console.style.height = '300px';
      }
    };
    minimizeTools.innerText = 'M';
    minimizeTools.style.padding = '4px';
    minimizeTools.style.borderRadius = '2px';
    minimizeTools.style.border = '1px solid white';
    minimizeTools.style.display = 'inline-block';
    minimizeTools.style.marginRight = '4px';
    minimizeTools.style.cursor = 'pointer';

    tools.appendChild(minimizeTools);

    this._console.id = 'dev-console';
    this._console.style.border = '1px solid white';
    this._console.style.position = 'absolute';
    this._console.style.zIndex = '100000';
    this._console.style.width = '500px';
    this._console.style.height = '300px';
    this._console.style.bottom = '10px';
    this._console.style.right = '10px';
    this._console.style.overflow = 'auto';
    this._console.style.borderRadius = '5px';
    this._console.style.padding = '4px';
    this._console.style.backgroundColor = 'rgb(0, 0, 0, 0.8)';
    this._console.style.color = 'white';

    this._console.appendChild(tools);
    document.body.appendChild(this._console);
    return this._console;
  }

  // The on-screen panel (setting display_logs): the last PANEL_LINES lines, errors in red.
  private _showInPanel(level: DeviceLogLevel, args: unknown[]) {
    try {
      if (!this._settings.display_logs) {
        this._hideLogs();
        return;
      }

      const panel = this._getConsole();
      const logRecord = document.createElement('div');
      logRecord.innerText = args
        .map((arg) =>
          typeof arg === 'string'
            ? arg
            : arg instanceof Error
              ? `${arg.name}: ${arg.message}`
              : this._safeJson(arg)
        )
        .join(' ');
      if (level === 'error' || level === 'warn') {
        logRecord.style.color = level === 'error' ? 'red' : 'orange';
      }
      panel.appendChild(logRecord);
      // the first child is the tools row
      while (panel.childElementCount > PANEL_LINES + 1) {
        panel.removeChild(panel.children[1]);
      }
    } catch {
      // The panel is a convenience; logging goes on without it.
    }
  }

  private _safeJson(value: unknown): string {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  // Only when it is shown: creating the panel just to remove it cost DOM work on every line.
  private _hideLogs() {
    if (!this._console) {
      return;
    }
    this._console.remove();
    this._console = null;
  }

  getTaxLogger(): TaxRegisterLogger {
    if (!this.taxLogger) {
      this.taxLogger = new TaxRegisterLogger(
        this._prroLogQueue,
        () => this._tenantAuth.tenantId,
        this._deviceLog
      );
    }

    return this.taxLogger;
  }
}

export interface ITaxRegisterLogger {
  logException(
    exception: any,
    severityLevel?: number,
    properties?: { [key: string]: any }
  ): void;
  logTrace(message: string, properties?: { [key: string]: any }): void;
}

// The Application Insights severity callers mark a warning with: a failure the work goes on
// after, like going offline. Any other severity of logException is an error.
const SEVERITY_WARNING = 2;

// The tax register (PRRO) logger: its entries go to the server's PRRO log (PrroLogService).
class TaxRegisterLogger implements ITaxRegisterLogger {
  constructor(
    private _queue: PrroLogQueue,
    private _getTenantId: () => string | undefined,
    private _deviceLog?: DeviceLogService
  ) {}

  logException(
    exception: any,
    severityLevel?: number,
    properties?: { [key: string]: any }
  ) {
    this._add(
      severityLevel === SEVERITY_WARNING ? 'Warning' : 'Error',
      exception,
      properties
    );
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this._add('Information', message, properties);
  }

  // Logging never fails the operation it logs.
  private _add(
    level: PrroLogLevel,
    value: unknown,
    properties?: { [key: string]: any }
  ) {
    try {
      this._queue.add(
        createPrroLogEntry(level, value, properties, this._getTenantId())
      );
    } catch {
      // The entry is lost; the operation goes on.
    }
    // Also in the device log, so the admin sees PRRO events among the rest.
    this._deviceLog?.add(
      level === 'Error' ? 'error' : level === 'Warning' ? 'warn' : 'info',
      'prro',
      [value],
      properties
    );
  }
}
